import { toUpdatePayload } from './toUpdatePayload'
import { applyPlayerApiError } from './applyPlayerApiError'
import { BE_TO_FORM_FIELD } from './beFieldMap'
import type { EditPlayerFormValues } from './types'

export interface PlayerSubmitDeps {
  playerId: string
  initialValues: EditPlayerFormValues
  /** Restrict the PUT to BE keys derived from these form fields. */
  fields?: (keyof EditPlayerFormValues)[]
  update: (args: {
    playerId: string
    payload: Record<string, unknown>
  }) => Promise<unknown>
  setErrors: (errors: Record<string, string>) => void
  /** Mark errored fields touched so web renders the red field layout (optional). */
  setTouched?: (touched: Record<string, boolean>) => void
  /** API key → human title for the summary toast (profile or roster map). */
  titleMap: Record<string, string>
  /**
   * Signal that the update succeeded so the caller can show its own
   * confirmation (web toast / mobile SuccessBottomSheet). Copy is owned by the
   * caller, not this shared core.
   */
  notifySuccess: () => void
  notifyError: (msg: string) => void
  /** Errored form-field keys → let the platform scroll/focus the first one. */
  onFieldErrors?: (fields: string[]) => void
  onSuccess?: () => void
}

/**
 * Form field → the BE payload key(s) `toUpdatePayload` emits for it. This MUST
 * track toUpdatePayload's output (STRING_FIELD_MAP + the special height/weight/
 * gender/address handling), because the section slice filters the payload by
 * these keys. A field missing here would be silently dropped from a scoped save.
 */
const FORM_FIELD_TO_BE_KEYS: Record<
  keyof EditPlayerFormValues,
  readonly string[]
> = {
  // Plain string fields (STRING_FIELD_MAP)
  firstName: ['name_first'],
  lastName: ['name_last'],
  birthDate: ['birth_date'],
  email: ['email'],
  phoneNumber: ['phone_number'],
  alternatePhoneNumber: ['alternate_phone_number'],
  preferredContactMethod: ['preferred_contact_method'],
  preferredLanguage: ['preferred_language'],
  emergencyContactName: ['emergency_contact_name'],
  emergencyContactRelationship: ['emergency_contact_relationship'],
  emergencyContactPhone: ['emergency_contact_phone_number'],
  instagram: ['instagram'],
  facebook: ['facebook'],
  tiktok: ['tiktok'],
  youtube: ['youtube'],
  leagueRegistrationNumber: ['league_registration_number'],
  hockeyUsaNumber: ['hockey_usa_number'],
  hockeyCanadaNumber: ['hockey_canada_number'],
  customMembershipNumber: ['custom_membership_number'],
  loyaltyNumber: ['loyalty_number'],
  // Specials
  heightFt: ['height'],
  heightIn: ['height'],
  weight: ['weight'],
  genderId: ['gender_id'],
  // Address sub-fields all collapse into the nested `address` object
  addressStreet1: ['address'],
  addressStreet2: ['address'],
  addressCity: ['address'],
  addressPostalCode: ['address'],
  addressCountryId: ['address'],
  addressProvinceId: ['address'],
  // UI-only image staging — never part of the PUT body
  imageUrl: [],
  imageData: [],
  imageDeleted: [],
}

/** BE keys the given form fields are allowed to write in a scoped save. */
const allowedBeKeys = (fields: (keyof EditPlayerFormValues)[]): Set<string> => {
  const allow = new Set<string>()
  fields.forEach((formKey) => {
    const beKeys = FORM_FIELD_TO_BE_KEYS[formKey] ?? []
    beKeys.forEach((beKey) => allow.add(beKey))
  })
  return allow
}

export const submitPlayer = async (
  values: EditPlayerFormValues,
  deps: PlayerSubmitDeps
): Promise<void> => {
  const {
    playerId,
    initialValues,
    fields,
    update,
    setErrors,
    setTouched,
    notifySuccess,
    notifyError,
    onFieldErrors,
    onSuccess,
  } = deps

  let payload = toUpdatePayload(values, initialValues) as Record<
    string,
    unknown
  >

  if (fields) {
    const allow = allowedBeKeys(fields)
    payload = Object.fromEntries(
      Object.entries(payload).filter(([beKey]) => allow.has(beKey))
    )
  }

  try {
    // Only hit the API + toast when something actually changed. A no-op save
    // still resolves (onSuccess) so callers can close/advance, but shows no
    // misleading "updated" toast.
    if (Object.keys(payload).length > 0) {
      await update({ playerId, payload })
      notifySuccess()
    }
    onSuccess?.()
  } catch (err: unknown) {
    applyPlayerApiError(err, {
      fieldMap: BE_TO_FORM_FIELD,
      titleMap: deps.titleMap,
      setErrors,
      setTouched,
      notifyError,
      onFieldErrors,
    })
  }
}
